import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  Alert,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useClubs } from '../context/AppContext';
import DiscussionService from '../services/discussionService';

const DISCUSSION_CATEGORIES = [
  { value: 'general', label: 'General', description: 'General discussions and chat', emoji: '💬' },
  { value: 'technical', label: 'Technical', description: 'Technical topics and help', emoji: '🔧' },
  { value: 'events', label: 'Events', description: 'Event planning and discussions', emoji: '📅' },
  { value: 'support', label: 'Support', description: 'Help and support requests', emoji: '🆘' },
] as const;

const CreateDiscussionPage: React.FC = () => {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const { clubs } = useClubs();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<'general' | 'technical' | 'events' | 'support'>('general');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const club = clubs.find(c => c.id === clubId);

  const handleSave = async () => {
    if (!title.trim() || !content.trim() || !clubId) {
      setError('Please fill in both title and content');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const discussionData = {
        title: title.trim(),
        content: content.trim(),
        category,
      };

      const newDiscussion = await DiscussionService.createDiscussion(clubId, discussionData);

      console.log('Created discussion:', newDiscussion); // Debug log

      // Navigate to the new discussion thread
      if (newDiscussion && newDiscussion.id) {
        navigate(`/clubs/${clubId}/discussions/${newDiscussion.id}`);
      } else {
        // If no ID, just navigate back to discussions list
        console.warn('Created discussion has no ID, navigating to discussions list');
        navigate(`/clubs/${clubId}/discussions`);
      }
    } catch (error: any) {
      console.error('Error creating discussion:', error);
      setError('Failed to create discussion. Please try again.');
      setSaving(false);
    }
  };

  const canSave = title.trim() && content.trim() && !saving;

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: { xs: 'white', sm: '#f0f2f5' },
      pb: { xs: 10, sm: 2 }
    }}>
      {/* Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        borderBottom: '1px solid #e4e6ea',
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
          flex: 1,
          minWidth: 0
        }}>
          <IconButton
            onClick={() => navigate(`/clubs/${clubId}/discussions`)}
            size="small"
            sx={{
              backgroundColor: '#f0f2f5',
              p: { xs: 1, sm: 1.2 },
              '&:hover': {
                backgroundColor: '#e4e6ea'
              }
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              Start Discussion
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {club?.name}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          sx={{
            textTransform: 'none',
            backgroundColor: '#1877f2',
            borderRadius: { xs: 2, sm: 1 },
            px: { xs: 2, sm: 3 },
            py: { xs: 0.8, sm: 1.2 },
            fontSize: { xs: '0.8rem', sm: '0.9rem' },
            fontWeight: 600,
            minWidth: { xs: 'auto', sm: 'auto' },
            '&:hover': {
              backgroundColor: '#166fe5'
            },
            '&:disabled': {
              backgroundColor: '#c7c7cc'
            }
          }}
        >
          {saving ? 'Starting...' : 'Start'}
        </Button>
      </Box>

      {/* Content */}
      <Box sx={{
        maxWidth: { xs: '100%', sm: 800 },
        mx: 'auto',
        px: { xs: 0, sm: 3 },
        py: { xs: 1, sm: 3 }
      }}>
        {error && (
          <Alert severity="error" sx={{
            mb: { xs: 2, sm: 3 },
            mx: { xs: 2, sm: 0 },
            borderRadius: { xs: 0, sm: 1 }
          }}>
            {error}
          </Alert>
        )}

        {/* Club Info */}
        <Card sx={{
          mb: { xs: 1, sm: 3 },
          mx: { xs: 0, sm: 0 },
          borderRadius: { xs: 0, sm: 3 },
          background: 'linear-gradient(135deg, #e8f4ff 0%, #f0f8ff 100%)',
          borderLeft: { xs: '4px solid #1877f2', sm: 'none' },
          border: { xs: 'none', sm: '2px solid rgba(24, 119, 242, 0.2)' },
          borderBottom: { xs: '1px solid #e4e6ea', sm: 'none' },
          boxShadow: { xs: 'none', sm: '0 4px 16px rgba(24, 119, 242, 0.1)' }
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 }
            }}>
              <Typography sx={{
                fontSize: { xs: '1.2rem', sm: '1.5rem' }
              }}>
                💭
              </Typography>
              <Typography variant="body1" sx={{
                color: '#1c1c1e',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 500
              }}>
                Starting a discussion in{' '}
                <Typography component="span" sx={{
                  fontWeight: 700,
                  color: '#1877f2'
                }}>
                  {club?.name}
                </Typography>
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Title Input */}
        <Card sx={{
          mb: { xs: 1, sm: 3 },
          mx: { xs: 0, sm: 0 },
          borderRadius: { xs: 0, sm: 3 },
          border: { xs: 'none', sm: '1px solid #e4e6ea' },
          borderBottom: { xs: '1px solid #e4e6ea', sm: 'none' },
          boxShadow: { xs: 'none', sm: '0 2px 8px rgba(0,0,0,0.08)' },
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: { xs: 'none', sm: '0 4px 16px rgba(0,0,0,0.12)' }
          }
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              mb: { xs: 1, sm: 1 },
              fontSize: { xs: '1rem', sm: '1.125rem' }
            }}>
              Discussion Title *
            </Typography>
            <TextField
              fullWidth
              placeholder="What would you like to discuss?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              inputProps={{ maxLength: 200 }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 500,
                  borderRadius: { xs: 2, sm: 2 },
                  backgroundColor: { xs: '#f8f9fa', sm: 'white' },
                  border: { xs: 'none', sm: '1px solid #e4e6ea' },
                  '&:hover': {
                    backgroundColor: { xs: '#f1f3f4', sm: 'white' }
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                    boxShadow: '0 0 0 2px rgba(24, 119, 242, 0.2)'
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: { xs: 'none', sm: '1px solid #e4e6ea' }
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{
              display: 'block',
              textAlign: 'right',
              fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }}>
              {title.length}/200
            </Typography>
          </CardContent>
        </Card>

        {/* Category Selection */}
        <Card sx={{
          mb: { xs: 1, sm: 3 },
          mx: { xs: 0, sm: 0 },
          borderRadius: { xs: 0, sm: 3 },
          border: { xs: 'none', sm: '1px solid #e4e6ea' },
          borderBottom: { xs: '1px solid #e4e6ea', sm: 'none' },
          boxShadow: { xs: 'none', sm: '0 2px 8px rgba(0,0,0,0.08)' },
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: { xs: 'none', sm: '0 4px 16px rgba(0,0,0,0.12)' }
          }
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              mb: { xs: 0.75, sm: 1 },
              fontSize: { xs: '1rem', sm: '1.125rem' }
            }}>
              Category
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.85rem', sm: '0.875rem' }
            }}>
              Choose the most appropriate category for your discussion
            </Typography>

            <FormControl>
              <RadioGroup
                value={category}
                onChange={(e) => setCategory(e.target.value as any)}
              >
                {DISCUSSION_CATEGORIES.map((cat) => (
                  <Card
                    key={cat.value}
                    sx={{
                      mb: { xs: 1, sm: 1.5 },
                      border: category === cat.value ? '2px solid #1877f2' : '1px solid #e5e5ea',
                      bgcolor: category === cat.value ? '#f0f8ff' : 'white',
                      cursor: 'pointer',
                      borderRadius: { xs: 1, sm: 1.5 },
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#1877f2',
                        boxShadow: '0 2px 8px rgba(24, 119, 242, 0.15)'
                      }
                    }}
                    onClick={() => setCategory(cat.value)}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <FormControlLabel
                        value={cat.value}
                        control={<Radio sx={{ display: 'none' }} />}
                        label=""
                        sx={{ m: 0, width: '100%', pointerEvents: 'none' }}
                      />
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: { xs: 0.25, sm: 0.5 }
                      }}>
                        <Typography sx={{
                          fontSize: { xs: '1.1rem', sm: '1.2rem' },
                          mr: { xs: 0.75, sm: 1 }
                        }}>
                          {cat.emoji}
                        </Typography>
                        <Typography variant="body1" sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          color: category === cat.value ? '#1877f2' : '#1c1c1e'
                        }}>
                          {cat.label}
                        </Typography>
                        {category === cat.value && (
                          <Box sx={{
                            ml: 'auto',
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#1877f2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography sx={{
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 600
                            }}>
                              ✓
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{
                        color: category === cat.value ? '#4a90e2' : '#8e8e93',
                        ml: { xs: 3, sm: 4 },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                      }}>
                        {cat.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>

        {/* Content Input */}
        <Card sx={{
          mb: { xs: 1, sm: 3 },
          mx: { xs: 0, sm: 0 },
          borderRadius: { xs: 0, sm: 3 },
          border: { xs: 'none', sm: '1px solid #e4e6ea' },
          borderBottom: { xs: '1px solid #e4e6ea', sm: 'none' },
          boxShadow: { xs: 'none', sm: '0 2px 8px rgba(0,0,0,0.08)' },
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: { xs: 'none', sm: '0 4px 16px rgba(0,0,0,0.12)' }
          }
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              mb: { xs: 0.75, sm: 1 },
              fontSize: { xs: '1rem', sm: '1.125rem' }
            }}>
              Message *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.85rem', sm: '0.875rem' }
            }}>
              Start the conversation with your message
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Share your thoughts, ask a question, or start a conversation..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  fontSize: { xs: '1rem', sm: '1rem' },
                  borderRadius: { xs: 2, sm: 2 },
                  backgroundColor: { xs: '#f8f9fa', sm: 'white' },
                  border: { xs: 'none', sm: '1px solid #e4e6ea' },
                  '&:hover': {
                    backgroundColor: { xs: '#f1f3f4', sm: 'white' }
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                    boxShadow: '0 0 0 2px rgba(24, 119, 242, 0.2)'
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: { xs: 'none', sm: '1px solid #e4e6ea' }
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{
              display: 'block',
              textAlign: 'right',
              fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }}>
              {content.length}/2000
            </Typography>
          </CardContent>
        </Card>

        {/* Guidelines */}
        <Card sx={{
          mb: { xs: 1, sm: 6 },
          mx: { xs: 0, sm: 0 },
          borderRadius: { xs: 0, sm: 2 },
          bgcolor: '#fff9e6',
          borderLeft: '4px solid #ffb800',
          border: { xs: 'none', sm: '1px solid #e4e6ea' },
          borderBottom: { xs: '1px solid #e4e6ea', sm: 'none' }
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: '1rem', sm: '1.125rem' }
            }}>
              💡 Discussion Guidelines
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 0.5 } }}>
              <Typography variant="body2" sx={{
                color: '#3c3c43',
                fontSize: { xs: '0.85rem', sm: '0.875rem' }
              }}>
                • Keep discussions relevant to the club and category
              </Typography>
              <Typography variant="body2" sx={{
                color: '#3c3c43',
                fontSize: { xs: '0.85rem', sm: '0.875rem' }
              }}>
                • Be respectful and constructive in your conversations
              </Typography>
              <Typography variant="body2" sx={{
                color: '#3c3c43',
                fontSize: { xs: '0.85rem', sm: '0.875rem' }
              }}>
                • Use clear titles that describe your topic
              </Typography>
              <Typography variant="body2" sx={{
                color: '#3c3c43',
                fontSize: { xs: '0.85rem', sm: '0.875rem' }
              }}>
                • Search existing discussions before creating new ones
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Bottom Actions - Mobile */}
      <Box sx={{
        display: { xs: 'flex', sm: 'none' },
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'white',
        borderTop: '1px solid #e5e5ea',
        p: { xs: 1.5, sm: 2 },
        gap: { xs: 1.5, sm: 2 },
        zIndex: 1000,
        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`/clubs/${clubId}/discussions`)}
          disabled={saving}
          sx={{
            flex: 1,
            textTransform: 'none',
            borderColor: '#e5e5ea',
            color: '#8e8e93',
            borderRadius: 2,
            py: 1.5,
            fontSize: '0.9rem',
            fontWeight: 500,
            '&:hover': {
              borderColor: '#1877f2',
              backgroundColor: 'rgba(24, 119, 242, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          sx={{
            flex: 2,
            textTransform: 'none',
            backgroundColor: '#1877f2',
            borderRadius: 2,
            py: 1.5,
            fontSize: '0.9rem',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#166fe5'
            },
            '&:disabled': {
              backgroundColor: '#c7c7cc',
              color: '#8a8d91'
            }
          }}
        >
          {saving ? 'Starting...' : 'Start Discussion'}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateDiscussionPage;